import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { TerrainType, GodPowerType } from '../../types/game';

interface Props {
  width: number;
  height: number;
}

// WebPopulous exact isometric projection
function calcPos(x: number, y: number, height: number, canvasWidth: number, canvasHeight: number): [number, number] {
  return [
    canvasWidth / 2 + x * 16 - y * 16,
    100 + x * 8 + y * 8 - height * 8  // Start from top with padding
  ];
}

// Convert screen coordinates to logical tile coordinates
function screenToLogical(screenX: number, screenY: number, canvasWidth: number, canvasHeight: number): [number, number] {
  // Adjust for the isometric offset (matching calcPos function)
  const offsetX = screenX - canvasWidth / 2;
  const offsetY = screenY - 100; // Starting Y offset from calcPos
  
  // Inverse of the isometric transformation
  // Original: screenX = x * 16 - y * 16
  // Original: screenY = x * 8 + y * 8
  // Solving for x and y:
  const x = (offsetX / 16 + offsetY / 8) / 2;
  const y = (offsetY / 8 - offsetX / 16) / 2;
  
  return [Math.floor(x), Math.floor(y)];
}

export const PopulousIsometric: React.FC<Props> = ({ width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { terrain, units, buildings, modifyTerrain, selectedPower, usePower, faith } = useGameStore();
  const [viewportOrigin, setViewportOrigin] = useState([0, 0]);
  const viewportWidth = Math.floor(width / 32) + 10;  // Dynamic viewport based on screen size
  const viewportHeight = Math.floor(height / 16) + 10;
  
  // Colors for different terrain types (WebPopulous style)
  const terrainColors = {
    land: '#007F00',
    ocean: '#0E27D6',
    left: '#009F00',
    right: '#005B00',
    up: '#00BF00',
    down: '#003F00'
  };
  
  // Calculate slope ID based on neighboring heights (from WebPopulous)
  const getSlopeId = (x: number, y: number): number => {
    if (!terrain || x < 0 || x >= terrain.length || y < 0 || y >= terrain[0].length) return 0;
    
    const h = terrain[x][y].height;
    const neighbors = {
      left: x > 0 ? terrain[x-1][y].height : h,
      right: x < terrain.length - 1 ? terrain[x+1][y].height : h,
      up: y > 0 ? terrain[x][y-1].height : h,
      down: y < terrain[0].length - 1 ? terrain[x][y+1].height : h
    };
    
    // WebPopulous slope calculation
    let slopeId = 0;
    if (neighbors.down > h) slopeId |= 1;
    if (neighbors.left > h) slopeId |= 2;
    if (neighbors.right > h) slopeId |= 4;
    if (neighbors.up > h) slopeId |= 8;
    
    return slopeId;
  };
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !terrain) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    
    // Draw terrain tiles in isometric order (back to front)
    for (let y = 0; y < viewportHeight; y++) {
      for (let x = 0; x < viewportWidth; x++) {
        const worldX = x + viewportOrigin[0];
        const worldY = y + viewportOrigin[1];
        
        if (worldX >= 0 && worldX < terrain.length && worldY >= 0 && worldY < terrain[0].length) {
          const cell = terrain[worldX][worldY];
          const [screenX, screenY] = calcPos(x, y, cell.height, width, height);
          
          // Draw diamond tile (WebPopulous style)
          ctx.save();
          ctx.translate(screenX, screenY);
          
          // Get terrain color based on slope
          const slopeId = getSlopeId(worldX, worldY);
          let color = terrainColors.land;
          
          if (cell.type === TerrainType.WATER || cell.height === 0) {
            color = terrainColors.ocean;
          } else if (slopeId !== 0) {
            // Use slope-based coloring
            const slopeColors = [
              terrainColors.land, terrainColors.down, terrainColors.left, terrainColors.down,
              terrainColors.right, terrainColors.down, terrainColors.right, terrainColors.down,
              terrainColors.up, terrainColors.left, terrainColors.up, terrainColors.left,
              terrainColors.up, terrainColors.right, terrainColors.up, terrainColors.land
            ];
            color = slopeColors[slopeId];
          }
          
          // Draw diamond shape
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(0, -8);     // Top
          ctx.lineTo(16, 0);     // Right
          ctx.lineTo(0, 8);      // Bottom
          ctx.lineTo(-16, 0);    // Left
          ctx.closePath();
          ctx.fill();
          
          // Draw height shading
          if (cell.height > 0) {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
            ctx.beginPath();
            ctx.moveTo(0, 8);      // Bottom
            ctx.lineTo(-16, 0);    // Left
            ctx.lineTo(-16, cell.height * 8);
            ctx.lineTo(0, 8 + cell.height * 8);
            ctx.closePath();
            ctx.fill();
            
            ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
            ctx.beginPath();
            ctx.moveTo(0, 8);      // Bottom
            ctx.lineTo(16, 0);     // Right
            ctx.lineTo(16, cell.height * 8);
            ctx.lineTo(0, 8 + cell.height * 8);
            ctx.closePath();
            ctx.fill();
          }
          
          ctx.restore();
        }
      }
    }
    
    // Draw buildings
    buildings.forEach(building => {
      const x = building.position[0] - viewportOrigin[0];
      const y = building.position[1] - viewportOrigin[1];
      
      if (x >= 0 && x < viewportWidth && y >= 0 && y < viewportHeight) {
        const [screenX, screenY] = calcPos(x, y, terrain[building.position[0]][building.position[1]].height, width, height);
        
        ctx.fillStyle = building.civilizationId === 'player' ? '#FF4444' : '#4444FF';
        ctx.fillRect(screenX - 8, screenY - 16, 16, 16);
        
        // Draw flag
        ctx.fillStyle = building.civilizationId === 'player' ? '#FF0000' : '#0000FF';
        ctx.fillRect(screenX + 4, screenY - 24, 4, 8);
      }
    });
    
    // Draw units
    units.forEach(unit => {
      const x = unit.position[0] - viewportOrigin[0];
      const y = unit.position[1] - viewportOrigin[1];
      
      if (x >= 0 && x < viewportWidth && y >= 0 && y < viewportHeight) {
        const [screenX, screenY] = calcPos(x, y, terrain[unit.position[0]][unit.position[1]].height, width, height);
        
        ctx.fillStyle = unit.civilizationId === 'player' ? '#FF8888' : '#8888FF';
        ctx.beginPath();
        ctx.arc(screenX, screenY - 4, 4, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Minimap is now rendered as a separate component in HUD
    
  }, [terrain, units, buildings, viewportOrigin, width, height]);
  
  // Handle mouse/touch events
  const handleInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !terrain) return;
    
    let x: number, y: number;
    if ('touches' in e) {
      // Touch event
      if (e.touches.length === 0) return;
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      // Mouse event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    // Handle terrain modification
    const [logicalX, logicalY] = screenToLogical(x, y, width, height);
    const worldX = logicalX + viewportOrigin[0];
    const worldY = logicalY + viewportOrigin[1];
      
      console.log('Click at screen:', x, y, '-> logical:', logicalX, logicalY, '-> world:', worldX, worldY);
      
      if (worldX >= 0 && worldX < terrain.length && worldY >= 0 && worldY < terrain[0].length) {
        const currentCell = terrain[worldX][worldY];
        const isRightClick = 'button' in e && e.button === 2;
        
        // Handle based on selected power or default terrain modification
        if (selectedPower) {
          console.log('Using power:', selectedPower, 'at', worldX, worldY);
          // Use the store's usePower method which handles faith costs
          usePower(selectedPower, { x: worldX, y: worldY });
        } else {
          // Default terrain modification only if we have faith for basic terraforming
          if (faith >= 5) { // Basic terraforming costs 5 faith
            const delta = isRightClick ? -1 : 1;
            const newHeight = Math.max(0, Math.min(50, currentCell.height + delta));
            console.log('Modifying terrain at', worldX, worldY, 'from height', currentCell.height, 'to', newHeight);
            // Should use a basic terraform power through usePower, but for now modify directly
            modifyTerrain(worldX, worldY, { height: newHeight });
          } else {
            console.log('Not enough faith for terraforming');
          }
        }
      } else {
        console.log('Click outside terrain bounds');
      }
  };
  
  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ 
        cursor: 'pointer',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        touchAction: 'none' // Prevent default touch behaviors
      }}
      onMouseDown={handleInteraction}
      onTouchStart={(e) => {
        e.preventDefault(); // Prevent default touch behavior
        handleInteraction(e);
      }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
};